import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import {
  FaArrowRight,
  FaEnvelope,
  FaEye,
  FaEyeSlash,
  FaHome,
  FaLock,
  FaPlayCircle,
  FaShieldAlt,
  FaSpinner,
  FaUserShield,
} from "react-icons/fa";

import { verifyUser } from "../../services/userServices";
import backgroundImage from "./HeaderSlider_03.jpg";

import "react-toastify/dist/ReactToastify.css";

const INITIAL_FORM = {
  email: "",
  password: "",
};

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-slate-50 py-3.5 pl-11 pr-4 text-sm font-medium text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-primary focus:bg-white focus:ring-4 focus:ring-orange-500/10 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-500 dark:focus:border-primary";

const labelClass =
  "mb-2 block text-sm font-bold text-slate-700 dark:text-slate-200";

export default function LoginPage() {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [showPassword, setShowPassword] = useState(false);
  const [loadingLogin, setLoadingLogin] = useState(false);
  const [loadingDemo, setLoadingDemo] = useState(false);

  const navigate = useNavigate();

  const saveUserAndRedirect = (user) => {
    localStorage.setItem("user", JSON.stringify(user));

    if (user?.token) {
      localStorage.setItem("token", user.token);
    }

    if (user?.accessToken) {
      localStorage.setItem("token", user.accessToken);
    }

    window.dispatchEvent(new Event("storage"));

    navigate("/appli/dashboard", {
      replace: true,
    });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.email.trim() || !formData.password.trim()) {
      toast.error("Merci de renseigner votre email et votre mot de passe.");
      return;
    }

    try {
      setLoadingLogin(true);

      const user = await verifyUser(
        formData.email.trim(),
        formData.password.trim()
      );

      if (user) {
        toast.success("Connexion réussie !");
        saveUserAndRedirect(user);
      } else {
        toast.error("Email ou mot de passe incorrect.");
      }
    } catch (error) {
      console.error("Erreur lors de la vérification de l'utilisateur :", error);
      toast.error("Erreur lors de la connexion. Veuillez réessayer.");
    } finally {
      setLoadingLogin(false);
    }
  };

  const handleDemoLogin = async () => {
    const demoCredentials = {
      email: "demo@gmail.com",
      password: "demo",
    };

    try {
      setLoadingDemo(true);

      const user = await verifyUser(
        demoCredentials.email,
        demoCredentials.password
      );

      if (user) {
        toast.success("Connexion au mode démonstration réussie !");
        saveUserAndRedirect(user);
      } else {
        toast.error("Impossible de se connecter au mode démonstration.");
      }
    } catch (error) {
      console.error("Erreur lors de la connexion en mode démo :", error);
      toast.error("Erreur lors de la connexion en mode démonstration.");
    } finally {
      setLoadingDemo(false);
    }
  };

  const isLoading = loadingLogin || loadingDemo;

  return (
    <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-slate-950 px-4 py-28 text-white sm:px-6 lg:px-8">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-25"
        style={{
          backgroundImage: `url(${backgroundImage})`,
        }}
      />

      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.30),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.22),transparent_32%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[size:44px_44px] opacity-20" />
      <div className="absolute inset-0 bg-slate-950/55" />

      <section className="relative grid w-full max-w-6xl gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
        <div className="hidden lg:block">
          <div className="overflow-hidden rounded-[2.5rem] border border-white/10 bg-white/10 p-8 shadow-2xl backdrop-blur-2xl">
            <div className="flex h-16 w-16 items-center justify-center rounded-[1.4rem] bg-white text-2xl text-primary shadow-xl">
              <FaUserShield />
            </div>

            <h1 className="mt-8 font-barlow text-5xl font-bold leading-tight tracking-tight text-white">
              Accéder à votre
              <br />
              espace club
            </h1>

            <p className="mt-5 max-w-lg text-base leading-8 text-white/70">
              Connectez-vous pour gérer les informations du club, les groupes,
              les inscriptions, les actualités et les contenus du site.
            </p>

            <div className="mt-8 grid gap-4">
              <div className="rounded-[1.5rem] border border-white/10 bg-white/10 p-5 backdrop-blur-xl">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/45">
                  Gestion simplifiée
                </p>
                <p className="mt-2 text-sm font-semibold leading-6 text-white/80">
                  Un tableau de bord pensé pour modifier facilement les contenus
                  visibles par les visiteurs.
                </p>
              </div>

              <div className="rounded-[1.5rem] border border-white/10 bg-white/10 p-5 backdrop-blur-xl">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/45">
                  Espace sécurisé
                </p>
                <p className="mt-2 text-sm font-semibold leading-6 text-white/80">
                  L’accès à l’administration est réservé aux utilisateurs
                  autorisés.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mx-auto w-full max-w-xl">
          <div className="overflow-hidden rounded-[2.5rem] border border-white/10 bg-white/95 p-6 text-slate-950 shadow-2xl backdrop-blur-2xl dark:bg-slate-900/95 dark:text-white sm:p-8">
            <div className="text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[1.4rem] bg-primary text-2xl text-white shadow-[0_18px_45px_rgba(242,113,49,0.32)]">
                <FaShieldAlt />
              </div>

              <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-orange-700 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
                Connexion
              </div>

              <h2 className="mt-5 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white">
                Espace administrateur
              </h2>

              <p className="mt-3 text-sm leading-6 text-slate-500 dark:text-slate-400">
                Identifiez-vous pour accéder au tableau de bord du club.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="mt-8 space-y-5">
              <div>
                <label htmlFor="email" className={labelClass}>
                  Email
                </label>

                <div className="relative">
                  <FaEnvelope className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="votre@email.com"
                    autoComplete="email"
                    disabled={isLoading}
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className={labelClass}>
                  Mot de passe
                </label>

                <div className="relative">
                  <FaLock className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`${inputClass} pr-12`}
                    placeholder="Votre mot de passe"
                    autoComplete="current-password"
                    disabled={isLoading}
                    required
                  />

                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-4 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-primary dark:hover:bg-white/10"
                    aria-label={
                      showPassword
                        ? "Masquer le mot de passe"
                        : "Afficher le mot de passe"
                    }
                    disabled={isLoading}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="inline-flex w-full items-center justify-center gap-3 rounded-full bg-primary px-7 py-3.5 text-sm font-bold text-white shadow-[0_18px_45px_rgba(242,113,49,0.30)] transition hover:-translate-y-0.5 hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loadingLogin ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Connexion...
                  </>
                ) : (
                  <>
                    Se connecter
                    <FaArrowRight />
                  </>
                )}
              </button>
            </form>

            <div className="my-7 flex items-center gap-4">
              <div className="h-px flex-1 bg-slate-200 dark:bg-white/10" />
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                ou
              </span>
              <div className="h-px flex-1 bg-slate-200 dark:bg-white/10" />
            </div>

            {/* <button
              type="button"
              onClick={handleDemoLogin}
              disabled={isLoading}
              className="inline-flex w-full items-center justify-center gap-3 rounded-full border border-slate-200 bg-white px-7 py-3.5 text-sm font-bold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-orange-200 hover:bg-orange-50 hover:text-orange-700 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10"
            >
              {loadingDemo ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Connexion démo...
                </>
              ) : (
                <>
                  <FaPlayCircle />
                  Accéder au mode démonstration
                </>
              )}
            </button> */}

            <div className="mt-6 text-center">
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 text-sm font-bold text-slate-500 transition hover:text-primary dark:text-slate-400"
              >
                <FaHome />
                Retour à l’accueil
              </Link>
            </div>
          </div>
        </div>
      </section>

      <ToastContainer
        position="bottom-right"
        autoClose={3500}
        theme="colored"
      />
    </main>
  );
}